// src/pages/ResetPassword.tsx
// Updated: 2026-08-20 15:00:00 CDT
// Purpose: Complete a Supabase recovery session by securely setting a new password.

import { useEffect, useState } from "react";
import RouteHeader from '@/components/RouteHeader';
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import PasswordFields from "@/components/PasswordFields";
import { supabase } from "@/integrations/supabase/client";
import { validateNewPassword } from "@/utils/passwordSecurity";
import { Film } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (isMounted) {
          setHasRecoverySession(Boolean(session));
        }
      } catch (error) {
        console.error("[ResetPassword] Unable to resolve recovery session:", error);
        if (isMounted) {
          setErrorMessage("This password-reset link could not be verified.");
        }
      } finally {
        if (isMounted) {
          setCheckingSession(false);
        }
      }
    };

    void checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.debug("[ResetPassword] Authentication event:", {
        event,
        hasSession: Boolean(session),
      });

      if (!isMounted) return;

      setHasRecoverySession(Boolean(session));
      setCheckingSession(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || !hasRecoverySession) return;
    setErrorMessage("");

    const validationError = validateNewPassword(password, confirmation);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        throw error;
      }

      console.debug("[ResetPassword] Password updated successfully.");

      const { error: signOutError } = await supabase.auth.signOut({ scope: "global" });
      if (signOutError) {
        console.warn("[ResetPassword] Password changed, but global sign-out failed:", signOutError);
      }

      navigate("/login?password=updated", { replace: true });
    } catch (error) {
      console.error("[ResetPassword] Password update failed:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to update the password. Request a new reset link and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background"><RouteHeader/><div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-linear-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md border-primary/10 bg-card/50 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mb-4 flex items-center justify-center space-x-2">
            <Film className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold">TableRead.tv</h1>
          </div>
          <CardTitle>Choose a new password</CardTitle>
          <CardDescription>
            Your reset link authorizes this one-time password change.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {checkingSession ? (
            <p className="text-sm text-muted-foreground" role="status">
              Verifying your password-reset link…
            </p>
          ) : !hasRecoverySession ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive" role="alert">
                {errorMessage || "This password-reset link is invalid or has expired."}
              </p>
              <Button asChild className="w-full">
                <Link to="/forgot-password">Request a new reset link</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordFields
                password={password}
                confirmation={confirmation}
                disabled={submitting}
                onPasswordChange={setPassword}
                onConfirmationChange={setConfirmation}
              />

              {errorMessage && (
                <p className="text-sm text-destructive" role="alert">
                  {errorMessage}
                </p>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Updating password…" : "Update password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div></div>
  );
};

export default ResetPassword;
